import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportQuery } from "@/transport"

import type { GetMetricLabelsRequest } from "@/types_api/backend/transport/get_metric_labels_request"
import type { MetricLabelResource } from "@/types_api/backend/transport/get_metric_labels_response"

const MAX_METRIC_LABEL_IDS = 1000

interface ConversationsMetricLabelsOptions {
  enabled?: boolean
  metricIds: string[]
}

function buildMetricLabelsRequest(
  metricIds: string[],
): GetMetricLabelsRequest | undefined {
  const normalizedMetricIds = Array.from(
    new Set(
      metricIds
        .map((metricId) => metricId.trim())
        .filter((metricId) => metricId.length > 0),
    ),
  )
    .sort()
    .slice(0, MAX_METRIC_LABEL_IDS)

  if (normalizedMetricIds.length === 0) {
    return undefined
  }

  return {
    metric_ids: normalizedMetricIds as [string, ...string[]],
  }
}

export function useConversationsMetricLabelsTransport({
  enabled = true,
  metricIds,
}: ConversationsMetricLabelsOptions) {
  const request = useMemo(
    () => buildMetricLabelsRequest(metricIds),
    [metricIds],
  )

  return useTransportQuery<
    "getMetricLabels",
    Record<string, MetricLabelResource>
  >({
    operation: "getMetricLabels",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && request !== undefined,
      select: (response) => response.metrics_by_id ?? {},
    },
  })
}
